import { css } from "@emotion/react";
import { Theme, useTheme } from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import { FaRegDotCircle } from "react-icons/fa";
import { FaRegCircleCheck } from "react-icons/fa6";
import { FaRegCircle } from "react-icons/fa6";
import { WiTime10 } from "react-icons/wi";
import { P, match } from "ts-pattern";

import { WrappedQuest } from "~/components/category/[id]/quest/lib";
import FussyPointLabel from "~/components/quest/FussyPointLabel";
import UserQuestProgressStatusLabel from "~/components/quest/UserQuestProgressStatusLabel";
import {
  buildQuestIconImageSrc,
  questPointFromFussyPoint,
} from "~/domains/quest/lib";
import {
  fixedHeight,
  fixedWidth,
  mediaQueryIsDesktop,
  mediaQueryIsMobile,
} from "~/lib/client/design";
import useIsSpMode from "~/lib/client/hooks/useIsSpMode";
import { UserQuestTaskProgressStatus } from "~/lib/graphql/client";
import { formatDateTime } from "~/lib/graphql/graphqlClient";
import { useCategoryDetailContext } from "~/pages/category/[id]";

const QuestCard = ({ quest }: { quest: WrappedQuest }) => {
  const theme = useTheme();
  const isSpMode = useIsSpMode() ?? false;
  const {
    props: { category },
  } = useCategoryDetailContext();

  const questIconSrc =
    quest.imageUrl ??
    buildQuestIconImageSrc(category, {
      kind: quest.point.pointKind,
      value: quest.point.pointValue,
    });

  return (
    <div css={rootStyle(theme)}>
      <Link css={questOutlineStyle(theme)} href={`/quest/${quest.id}`}>
        <div css={questOutlineInnerStyle}>
          {questIconSrc.endsWith(".gif") ? (
            <img
              src={questIconSrc}
              alt={"quest image"}
              height={isSpMode ? 60 : 160}
              width={isSpMode ? 60 : 160}
            />
          ) : (
            <Image
              src={questIconSrc}
              alt={"quest image"}
              height={isSpMode ? 60 : 160}
              width={isSpMode ? 60 : 160}
            />
          )}
          <div css={questDetailStyle}>
            <p css={questDetailTextStyle}>
              <span css={questDetailBoldTextStyle}>
                {quest.point.pointKindJaLabel}
              </span>
              <UserQuestProgressStatusLabel status={quest.progress.status} />
            </p>
            <p css={questDetailTitleTextStyle}>{quest.title}</p>
            <div>
              <FussyPointLabel point={questPointFromFussyPoint(quest.point)} />
              <p css={questDetailUserNameStyle(theme)}>{quest.user.name}</p>
              <p css={questDetailTermStyle}>
                {formatDateTime(quest.startAt)} ~ {formatDateTime(quest.endAt)}
              </p>
            </div>
          </div>
        </div>
      </Link>

      <div css={questTasksContainerStyle(theme)}>
        <Link css={questTasksListStyle} href={`/quest/${quest.id}`}>
          {quest.tasks
            .sort((a, b) => a.position - b.position)
            .map((task, index) => (
              <div key={index} css={questTaskChildrenStyle(theme)}>
                <UserQuestTaskProgressCircle status={task.progress.status} />
                <span>{task.title}</span>
              </div>
            ))}
        </Link>
      </div>
    </div>
  );
};

const UserQuestTaskProgressCircle = ({
  status,
}: {
  status: UserQuestTaskProgressStatus;
}) => {
  const theme = useTheme();
  const iconColor = match(status)
    .with(
      P.union(
        UserQuestTaskProgressStatus.NotRequestedYet,
        UserQuestTaskProgressStatus.NotStartedYet
      ),
      () => "#777777"
    )
    .with(UserQuestTaskProgressStatus.Doing, () => theme.palette.primary.main)
    .with(UserQuestTaskProgressStatus.UnderReviewing, () => "#1C1B1F")
    .with(
      UserQuestTaskProgressStatus.Verified,
      () => theme.palette.secondary.main
    )
    .exhaustive();
  const icon = match(status)
    .with(
      P.union(
        UserQuestTaskProgressStatus.NotRequestedYet,
        UserQuestTaskProgressStatus.NotStartedYet
      ),
      () => <FaRegCircle />
    )
    .with(UserQuestTaskProgressStatus.Doing, () => <FaRegDotCircle />)
    .with(UserQuestTaskProgressStatus.UnderReviewing, () => <WiTime10 />)
    .with(UserQuestTaskProgressStatus.Verified, () => <FaRegCircleCheck />)
    .exhaustive();

  return <div css={iconOutLineStyle(iconColor)}>{icon}</div>;
};

const iconOutLineStyle = (iconColor: string) => css`
  ${fixedWidth("1.25rem")}
  ${fixedHeight("1.25rem")}

  border-radius: 50%;
  color: ${iconColor};
`;

const rootStyle = (theme: Theme) => css`
  display: flex;
  background: white;

  ${mediaQueryIsMobile(css`
    flex-direction: column;
  `)};

  ${mediaQueryIsDesktop(css`
    width: fit-content;
    flex-direction: column;
    border: 1px solid ${theme.palette.divider};
  `)};
`;

const questOutlineStyle = (theme: Theme) => css`
  background-color: ${theme.palette.background.paper};
  flex-shrink: 0;

  ${mediaQueryIsMobile(css`
    width: 100%;
    padding: 1rem;
  `)};

  ${mediaQueryIsDesktop(css`
    width: 30rem;
    padding: 2.5rem 2rem 2.5rem;
  `)};
`;

const questOutlineInnerStyle = css`
  position: sticky;
  top: 2rem;
  display: flex;
  flex-flow: row;
  gap: 1rem;
`;

const questDetailStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const questDetailTextStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0;
  font-size: 0.75rem;
`;

const questDetailBoldTextStyle = css`
  font-weight: 700;
`;

const questDetailTitleTextStyle = css`
  flex: 1;
  display: -webkit-box;
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  overflow: hidden;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const questDetailUserNameStyle = (theme: Theme) => css`
  margin: 0;
  font-size: 0.625rem;
  font-weight: 700;
  color: ${theme.palette.text.primary};
`;

const questDetailTermStyle = css`
  margin: 0;
  font-size: 0.5625rem;
  font-weight: 400;
`;

const questTasksContainerStyle = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  padding: 1rem;

  ${mediaQueryIsMobile(css`
    width: 100%;
    background-color: ${theme.palette.background.paper};
  `)};

  ${mediaQueryIsDesktop(css`
    width: 30rem;
  `)};
`;

const questTasksListStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const questTaskChildrenStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  padding: 0.75rem 1rem 0.75rem 1rem;
  border-radius: 0.25rem;
  font-size: 0.8rem;
  font-weight: bold;

  ${mediaQueryIsDesktop(css`
    background: ${theme.palette.background.paper};
  `)};

  ${mediaQueryIsMobile(css`
    background: ${theme.palette.background.default};
  `)};
`;

export default QuestCard;
